import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ShoppingCart, Plus, X, ExternalLink, Download, AlertTriangle, CheckCircle, Clock, CreditCard, ChevronDown, ChevronUp, Trash2, Calculator } from 'lucide-react';

const STATUS_CONFIG = {
  pending:         { label: 'Pending Review',   color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  acknowledged:    { label: 'Acknowledged',     color: 'bg-blue-100 text-blue-700',    icon: CheckCircle },
  processing:      { label: 'Processing',       color: 'bg-purple-100 text-purple-700', icon: Clock },
  payment_pending: { label: 'Payment Pending',  color: 'bg-orange-100 text-orange-700', icon: CreditCard },
  advance_pending: { label: 'Advance Pending',  color: 'bg-pink-100 text-pink-700',    icon: CreditCard },
  completed:       { label: 'Completed',        color: 'bg-green-100 text-green-700',  icon: CheckCircle },
  cancelled:       { label: 'Cancelled',        color: 'bg-red-100 text-red-600',      icon: AlertTriangle },
};
const PM_OPTIONS = [
  { value:'bank_transfer', label:'Bank Transfer' },
  { value:'cheque',        label:'Cheque' },
  { value:'upi',           label:'UPI' },
  { value:'cash',          label:'Cash' },
  { value:'other',         label:'Other' },
];

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; }
}
function fmtCur(n) {
  return '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

const emptyItem = { item_name: '', quantity: '', taxable_value: '', tax_rate: '', cgst: '', sgst: '', igst: '' };

const emptyForm = {
  po_number: '',
  order_date: new Date().toISOString().slice(0,10),
  due_date: '',
  remark: '',
  project_name: '',
  pdf: null,
  items: [{ ...emptyItem }],
};

// Per-item total
function itemTotal(it) {
  const tv = parseFloat(it.taxable_value) || 0;
  const cg = parseFloat(it.cgst) || 0;
  const sg = parseFloat(it.sgst) || 0;
  const ig = parseFloat(it.igst) || 0;
  return tv + cg + sg + ig;
}

export default function ClientPurchaseOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({});
  const [showPayment, setShowPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const downloadPdf = async (url, filename) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(blobUrl);
    } catch { toast.error('Download failed. Try opening directly.'); }
  };

  const openPdf = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch { toast.error('Could not open PDF. Try downloading instead.'); }
  };

  const deletePO = async (id, poNumber) => {
    if (!window.confirm(`Delete Purchase Order "${poNumber}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/client/purchase-orders/${id}`);
      toast.success('Purchase order deleted');
      setExpandedId(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
    finally { setDeletingId(null); }
  };

  const [form, setForm] = useState({ ...emptyForm, items: [{ ...emptyItem }] });

  // ── Multi-item helpers ────────────────────────────────────────────────────
  const updateItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      const it = { ...items[idx], [field]: val };
      // Auto-recalculate tax when taxable_value or tax_rate changes
      if (field === 'taxable_value' || field === 'tax_rate') {
        const tv = parseFloat(field === 'taxable_value' ? val : it.taxable_value) || 0;
        const rate = parseFloat(field === 'tax_rate' ? val : it.tax_rate) || 0;
        const totalTax = parseFloat(((tv * rate) / 100).toFixed(2));
        const half = parseFloat((totalTax / 2).toFixed(2));
        if (it.igst) { it.igst = String(totalTax); it.cgst = ''; it.sgst = ''; }
        else { it.cgst = String(half); it.sgst = String(half); it.igst = ''; }
      }
      // Mutual exclusion: entering CGST/SGST clears IGST and vice versa
      if (field === 'cgst' || field === 'sgst') { it.igst = ''; }
      if (field === 'igst') { it.cgst = ''; it.sgst = ''; }
      items[idx] = it;
      return { ...f, items };
    });
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Grand total across all items
  const grandTotal = form.items.reduce((s, it) => s + itemTotal(it), 0);

  const [payForm, setPayForm] = useState({
    payment_date: new Date().toISOString().slice(0,10),
    amount: '', payment_mode: 'bank_transfer', bank_name: '', cheque_number: '', utr_reference: '', remark: '',
  });

  const load = () => {
    setLoading(true);
    api.get('/client/purchase-orders')
      .then(r => setOrders(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadDetail = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (expandedData[id]) return;
    try {
      const r = await api.get(`/client/purchase-orders/${id}`);
      setExpandedData(d => ({ ...d, [id]: r.data }));
    } catch { toast.error('Failed to load details'); }
  };

  const submitPO = async () => {
    if (!form.po_number.trim()) return toast.error('PO Number is required');
    if (!form.order_date) return toast.error('Order date is required');
    if (!form.pdf) return toast.error('PDF file is required');
    const validItems = form.items.filter(it => it.item_name.trim());
    if (!validItems.length) return toast.error('Please add at least one item');

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('po_number', form.po_number.trim());
      fd.append('order_date', form.order_date);
      if (form.due_date) fd.append('due_date', form.due_date);
      if (form.remark) fd.append('remark', form.remark);
      if (form.project_name) fd.append('project_name', form.project_name);
      fd.append('items_json', JSON.stringify(validItems));
      fd.append('total_amount', grandTotal.toFixed(2));
      // Also send first item's fields for backward compat
      if (validItems[0]) {
        if (validItems[0].item_name) fd.append('item_name', validItems[0].item_name);
        if (validItems[0].quantity) fd.append('quantity', validItems[0].quantity);
        if (validItems[0].taxable_value) fd.append('taxable_value', validItems[0].taxable_value);
        if (validItems[0].tax_rate) fd.append('tax_rate', validItems[0].tax_rate);
        if (validItems[0].cgst) fd.append('cgst', validItems[0].cgst);
        if (validItems[0].sgst) fd.append('sgst', validItems[0].sgst);
        if (validItems[0].igst) fd.append('igst', validItems[0].igst);
      }
      fd.append('pdf', form.pdf);

      await api.post('/client/purchase-orders', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Purchase order submitted successfully!');
      setShowCreate(false);
      setForm({ ...emptyForm, order_date: new Date().toISOString().slice(0,10), items: [{ ...emptyItem }] });
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  const submitPayment = async () => {
    if (!payForm.payment_date || !payForm.amount) return toast.error('Date and amount are required');
    setSubmitting(true);
    try {
      const r = await api.post(`/client/purchase-orders/${showPayment}/payments`, payForm);
      toast.success(r.data.is_advance ? 'Advance payment recorded' : 'Payment recorded');
      setShowPayment(null);
      setPayForm({ payment_date:new Date().toISOString().slice(0,10), amount:'', payment_mode:'bank_transfer', bank_name:'', cheque_number:'', utr_reference:'', remark:'' });
      const detail = await api.get(`/client/purchase-orders/${showPayment}`);
      setExpandedData(d => ({ ...d, [showPayment]: detail.data }));
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900">Purchase Orders</h1>
          <p className="text-gray-400 text-sm mt-0.5">Submit and track your purchase orders</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus size={16}/> New Purchase Order
        </button>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/></div>
      ) : orders.length === 0 ? (
        <div className="card py-16 text-center">
          <ShoppingCart className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
          <p className="text-gray-500 font-medium">No purchase orders yet</p>
          <p className="text-gray-400 text-sm mt-1">Click "New Purchase Order" to submit your first order</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => {
            const sc = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
            const Icon = sc.icon;
            const isOpen = expandedId === o.id;
            const detail = expandedData[o.id];
            const paid = Number(o.paid_amount || 0);
            const total = Number(o.total_amount || 0);
            const bal = total - paid;
            const paidPct = total > 0 ? Math.min(Math.round(paid/total*100), 100) : 0;

            return (
              <div key={o.id} className="card overflow-hidden">
                {/* Main row */}
                <div className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => loadDetail(o.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-bold text-gray-900 font-mono text-sm">{o.po_number}</p>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${sc.color}`}>
                        <Icon className="w-3 h-3"/> {sc.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 flex-wrap text-xs text-gray-400">
                      <span>Order: {fmt(o.order_date)}</span>
                      {o.due_date && <span className={new Date(o.due_date)<new Date()&&o.status!=='completed'?'text-red-500 font-semibold':''}>Due: {fmt(o.due_date)}</span>}
                      {o.remark && <span className="truncate max-w-48">{o.remark}</span>}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right flex-shrink-0">
                    {total > 0 && (
                      <>
                        <p className="text-sm font-bold text-gray-900">{fmtCur(total)}</p>
                        <p className={`text-xs font-semibold ${bal > 0 ? 'text-red-500':'text-green-600'}`}>
                          {bal > 0 ? `Balance: ${fmtCur(bal)}` : 'Paid ✓'}
                        </p>
                      </>
                    )}
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0"/> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0"/>}
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-4 space-y-4">
                    {/* Payment progress bar */}
                    {total > 0 && (
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>Payment Progress</span>
                          <span>{paidPct}% ({fmtCur(paid)} of {fmtCur(total)})</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-2 rounded-full bg-green-500 transition-all" style={{width:`${paidPct}%`}}/>
                        </div>
                      </div>
                    )}

                    {/* PDF link */}
                    {o.pdf_url && (
                      <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-800">Purchase Order PDF</p>
                          <p className="text-xs text-blue-500">Your submitted document</p>
                        </div>
                        <button
                          onClick={() => openPdf(o.pdf_url)}
                          className="btn-secondary" style={{fontSize:12,padding:'6px 12px'}}
                        >
                          <ExternalLink size={12}/> Open
                        </button>
                        <button
                          onClick={() => downloadPdf(o.pdf_url, `PO-${o.po_number}.pdf`)}
                          className="btn-secondary" style={{fontSize:12,padding:'6px 12px'}}
                        >
                          <Download size={12}/> Download
                        </button>
                      </div>
                    )}

                    {/* Payments */}
                    {detail?.payments && detail.payments.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-gray-600 mb-2">Payment History</p>
                        <div className="space-y-2">
                          {detail.payments.map(p => (
                            <div key={p.id} className={`flex items-center justify-between rounded-xl px-4 py-2.5 ${p.is_advance?'bg-purple-50':'bg-green-50'}`}>
                              <div>
                                <span className="text-sm font-bold text-gray-900">{fmtCur(p.amount)}</span>
                                {p.is_advance && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">Advance</span>}
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {p.payment_mode?.replace('_',' ')} · {fmt(p.payment_date)}
                                  {p.utr_reference && ` · UTR: ${p.utr_reference}`}
                                </p>
                              </div>
                              <button onClick={async () => {
                                if (!window.confirm('Delete this payment?')) return;
                                try {
                                  await api.delete(`/client/purchase-orders/${o.id}/payments/${p.id}`);
                                  toast.success('Payment deleted');
                                  const r2 = await api.get(`/client/purchase-orders/${o.id}`);
                                  setExpandedData(d => ({...d, [o.id]: r2.data}));
                                  load();
                                } catch { toast.error('Failed'); }
                              }} className="text-red-400 hover:text-red-600 p-1"><X size={14}/></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add payment button */}
                    {o.status !== 'cancelled' && o.status !== 'completed' && bal > 0 && (
                      <button onClick={() => setShowPayment(o.id)} className="btn-secondary w-full text-sm">
                        <CreditCard size={15}/> Add Payment
                      </button>
                    )}

                    {/* Delete PO */}
                    {o.status === 'pending' && (
                      <button
                        onClick={() => deletePO(o.id, o.po_number)}
                        disabled={deletingId === o.id}
                        className="w-full flex items-center justify-center gap-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-xl py-2 transition-colors"
                      >
                        <Trash2 size={14}/>
                        {deletingId === o.id ? 'Deleting...' : 'Delete Purchase Order'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create PO Modal */}
      {showCreate && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:580, display:'flex', flexDirection:'column', maxHeight:'90vh'}}>
              <div className="modal-header" style={{flexShrink:0}}>
                <div><h2>New Purchase Order</h2><p>Submit a purchase order with PDF document</p></div>
                <button className="modal-close" onClick={()=>setShowCreate(false)}><X size={16}/></button>
              </div>
              <div className="modal-body space-y-4" style={{overflowY:'auto', flex:1}}>

                {/* Basic PO Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div style={{gridColumn:'1/-1'}}>
                    <label className="field-label">PO Number *</label>
                    <input className="field-input" placeholder="e.g. PO-2024-001" value={form.po_number} onChange={e=>setForm(f=>({...f,po_number:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="field-label">Order Date *</label>
                    <input type="date" className="field-input" value={form.order_date} onChange={e=>setForm(f=>({...f,order_date:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="field-label">Due Date</label>
                    <input type="date" className="field-input" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))}/>
                  </div>
                </div>

                {/* Project Name */}
                <div>
                  <label className="field-label">Project Name</label>
                  <input className="field-input" placeholder="Enter project name..."
                    value={form.project_name} onChange={e=>setForm(f=>({...f,project_name:e.target.value}))}/>
                </div>

                {/* Items Table */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Items</p>
                    <button onClick={addItem}
                      className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 hover:bg-teal-50 px-2.5 py-1.5 rounded-lg transition-colors border border-teal-200">
                      <Plus size={12}/> Add Item
                    </button>
                  </div>

                  {form.items.map((it, idx) => (
                    <div key={idx} className={`p-3 space-y-2 ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-gray-500">Item #{idx + 1}</span>
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(idx)}
                            className="text-red-400 hover:text-red-600 p-1 rounded transition-colors">
                            <X size={13}/>
                          </button>
                        )}
                      </div>

                      {/* Item Name */}
                      <input className="field-input" placeholder="Item name *"
                        value={it.item_name} onChange={e => updateItem(idx, 'item_name', e.target.value)}/>

                      {/* Qty + Taxable Value + Tax Rate */}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="field-label">Qty</label>
                          <input type="number" className="field-input" placeholder="0"
                            value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}/>
                        </div>
                        <div>
                          <label className="field-label">Taxable Val (₹)</label>
                          <input type="number" className="field-input" placeholder="0.00"
                            value={it.taxable_value} onChange={e => updateItem(idx, 'taxable_value', e.target.value)}/>
                        </div>
                        <div>
                          <label className="field-label">Tax Rate (%)</label>
                          <input type="number" className="field-input" placeholder="18"
                            value={it.tax_rate} onChange={e => updateItem(idx, 'tax_rate', e.target.value)}/>
                        </div>
                      </div>

                      {/* Tax: CGST+SGST or IGST */}
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-2.5 space-y-1.5">
                        <p className="text-xs font-semibold text-blue-600">Tax Mode — CGST+SGST or IGST</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="field-label">CGST (₹)</label>
                            <input type="number" placeholder="0.00"
                              disabled={!!it.igst}
                              className={`field-input ${it.igst ? 'opacity-40 cursor-not-allowed' : ''}`}
                              value={it.cgst} onChange={e => updateItem(idx, 'cgst', e.target.value)}/>
                          </div>
                          <div>
                            <label className="field-label">SGST (₹)</label>
                            <input type="number" placeholder="0.00"
                              disabled={!!it.igst}
                              className={`field-input ${it.igst ? 'opacity-40 cursor-not-allowed' : ''}`}
                              value={it.sgst} onChange={e => updateItem(idx, 'sgst', e.target.value)}/>
                          </div>
                          <div>
                            <label className="field-label">IGST (₹)</label>
                            <input type="number" placeholder="0.00"
                              disabled={!!(it.cgst || it.sgst)}
                              className={`field-input ${(it.cgst || it.sgst) ? 'opacity-40 cursor-not-allowed' : ''}`}
                              value={it.igst} onChange={e => updateItem(idx, 'igst', e.target.value)}/>
                          </div>
                        </div>
                      </div>

                      {/* Per-item total */}
                      <div className="flex justify-end">
                        <span className="text-xs text-gray-500">Item Total: </span>
                        <span className="text-xs font-bold text-gray-800 ml-1">{fmtCur(itemTotal(it))}</span>
                      </div>
                    </div>
                  ))}

                  {/* Grand Total */}
                  <div className="flex items-center justify-between bg-amber-50 px-4 py-3 border-t border-amber-200">
                    <div className="flex items-center gap-2">
                      <Calculator size={14} className="text-amber-600"/>
                      <span className="text-sm font-bold text-amber-800">Grand Total</span>
                      <span className="text-xs text-amber-600">({form.items.length} item{form.items.length > 1 ? 's' : ''})</span>
                    </div>
                    <span className="text-base font-black text-amber-900">{fmtCur(grandTotal)}</span>
                  </div>
                </div>

                {/* Remark */}
                <div>
                  <label className="field-label">Remark / Notes</label>
                  <textarea className="field-input" rows={2} style={{resize:'none'}}
                    placeholder="Any notes for this order..."
                    value={form.remark} onChange={e=>setForm(f=>({...f,remark:e.target.value}))}/>
                </div>

                {/* PDF Upload */}
                <div>
                  <label className="field-label">Upload PO Document (PDF) *</label>
                  <div className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${form.pdf?'border-green-400 bg-green-50':'border-gray-200 hover:border-amber-400 hover:bg-amber-50'}`}
                    onClick={()=>document.getElementById('po-pdf-input').click()}>
                    {form.pdf ? (
                      <div>
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2"/>
                        <p className="text-sm font-semibold text-green-700">{form.pdf.name}</p>
                        <p className="text-xs text-green-500">{(form.pdf.size/1024/1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div>
                        <ShoppingCart className="w-8 h-8 text-gray-300 mx-auto mb-2"/>
                        <p className="text-sm text-gray-500">Click to upload PDF</p>
                        <p className="text-xs text-gray-400">Max 20MB, PDF only</p>
                      </div>
                    )}
                  </div>
                  <input id="po-pdf-input" type="file" accept=".pdf,application/pdf" className="hidden"
                    onChange={e => { if(e.target.files[0]) setForm(f=>({...f,pdf:e.target.files[0]})); }}/>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" style={{flex:1}} onClick={()=>setShowCreate(false)}>Cancel</button>
                <button className="btn-primary" style={{flex:1}} onClick={submitPO} disabled={submitting}>
                  {submitting ? 'Uploading...' : 'Submit Purchase Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPayment && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:460}}>
              <div className="modal-header">
                <div><h2>Add Payment</h2><p>Record a payment for this purchase order</p></div>
                <button className="modal-close" onClick={()=>setShowPayment(null)}><X size={16}/></button>
              </div>
              <div className="modal-body space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="field-label">Payment Date *</label>
                    <input type="date" className="field-input" value={payForm.payment_date} onChange={e=>setPayForm(f=>({...f,payment_date:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="field-label">Amount *</label>
                    <input type="number" className="field-input" placeholder="₹ 0.00" value={payForm.amount} onChange={e=>setPayForm(f=>({...f,amount:e.target.value}))}/>
                  </div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label className="field-label">Payment Mode</label>
                    <select className="field-input" value={payForm.payment_mode} onChange={e=>setPayForm(f=>({...f,payment_mode:e.target.value}))}>
                      {PM_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {payForm.payment_mode === 'bank_transfer' || payForm.payment_mode === 'upi' ? (
                    <>
                      <div>
                        <label className="field-label">Bank Name</label>
                        <input className="field-input" placeholder="e.g. HDFC Bank" value={payForm.bank_name} onChange={e=>setPayForm(f=>({...f,bank_name:e.target.value}))}/>
                      </div>
                      <div>
                        <label className="field-label">UTR / Reference No.</label>
                        <input className="field-input" placeholder="UTR number" value={payForm.utr_reference} onChange={e=>setPayForm(f=>({...f,utr_reference:e.target.value}))}/>
                      </div>
                    </>
                  ) : payForm.payment_mode === 'cheque' ? (
                    <>
                      <div>
                        <label className="field-label">Bank Name</label>
                        <input className="field-input" placeholder="e.g. HDFC Bank" value={payForm.bank_name} onChange={e=>setPayForm(f=>({...f,bank_name:e.target.value}))}/>
                      </div>
                      <div>
                        <label className="field-label">Cheque Number</label>
                        <input className="field-input" placeholder="Cheque no." value={payForm.cheque_number} onChange={e=>setPayForm(f=>({...f,cheque_number:e.target.value}))}/>
                      </div>
                    </>
                  ) : null}
                  <div style={{gridColumn:'1/-1'}}>
                    <label className="field-label">Remark</label>
                    <input className="field-input" placeholder="Optional remark" value={payForm.remark} onChange={e=>setPayForm(f=>({...f,remark:e.target.value}))}/>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                  💡 If payment date is before the PO order date, it will be marked as <strong>Advance Payment</strong> automatically.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" style={{flex:1}} onClick={()=>setShowPayment(null)}>Cancel</button>
                <button className="btn-primary" style={{flex:1}} onClick={submitPayment} disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
